
import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, get } from 'firebase/database';
import MainLayout from '../components/MainLayout';
import { useAuth } from '../context/AuthContext';

const ViewCBTTestScores = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState(null);
  const [allClasses, setAllClasses] = useState([]);
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('FIRST TERM');
  const [selectedSession, setSelectedSession] = useState(
    `${new Date().getFullYear()}/${new Date().getFullYear() + 1}`
  );
  const [cbtScores, setCbtScores] = useState([]);
  const [loading, setLoading] = useState(false);
  const db = getDatabase();

  const sessions = Array.from({ length: 5 }, (_, i) => {
    const year = new Date().getFullYear() - i;
    return `${year}/${year + 1}`;
  });
  const terms = ['FIRST TERM', 'SECOND TERM', 'THIRD TERM'];

  // Fetch user data
  useEffect(() => {
    if (currentUser) {
      const userRef = ref(db, `users/${currentUser.uid}`);
      onValue(userRef, (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setUserData(data);
          if (['teacher', 'form-master'].includes(data.role?.toLowerCase())) {
            setSelectedClass(data.assignedClass || '');
            if (data.assignedSubjects) setAssignedSubjects(data.assignedSubjects);
          }
        }
      });
    }
  }, [currentUser, db]);

  // Load all classes for Admin/Principal
  useEffect(() => {
    if (userData?.role?.toLowerCase() === 'admin' || userData?.role?.toLowerCase() === 'principal') {
      const classesRef = ref(db, 'classes');
      onValue(classesRef, (snapshot) => {
        setAllClasses(snapshot.exists() ? Object.keys(snapshot.val()).sort() : []);
      });
    }
  }, [userData, db]);

  // Load subjects when class changes
  useEffect(() => {
    const role = userData?.role?.toLowerCase();
    if (!selectedClass || !role) return;

    if (['admin', 'principal'].includes(role)) {
        const subjectsRef = ref(db, `classSubjects/${selectedClass}`);
        onValue(subjectsRef, (snapshot) => {
            const data = snapshot.val();
            setAssignedSubjects(Array.isArray(data) ? data : Object.keys(data || {}));
        });
    } else if (['teacher', 'form-master'].includes(role) && userData.assignedSubjects) {
        setAssignedSubjects(userData.assignedSubjects);
    }
  }, [selectedClass, userData, db]);


  const handleSearch = async () => {
    if (!selectedClass || !selectedSubject || !selectedSession || !selectedTerm) {
      alert('Please select a session, term, class, and subject.');
      return;
    }
    setLoading(true);
    setCbtScores([]);

    try {
      const sessionPath = selectedSession.replace('/', '-');
      const scoresRef = ref(db, `cbt_test_scores/${sessionPath}/${selectedTerm}/${selectedClass}/${selectedSubject}`);
      const scoresSnap = await get(scoresRef);

      if (scoresSnap.exists()) {
        const scoresData = scoresSnap.val();
        const usersRef = ref(db, 'users');
        const usersSnap = await get(usersRef);
        const usersData = usersSnap.val() || {};

        const scoresWithNames = Object.keys(scoresData).map(studentId => {
            const student = usersData[studentId];
            return {
                id: studentId,
                name: student ? student.fullName || 'N/A' : 'Unknown Student',
                regNo: student ? student.regNo || 'N/A' : 'N/A',
                score: scoresData[studentId].score,
                totalQuestions: scoresData[studentId].totalQuestions,
                timestamp: new Date(scoresData[studentId].timestamp).toLocaleString(),
            };
        });
        setCbtScores(scoresWithNames);
      } else {
        alert("No CBT scores found for the selected criteria.");
      }
    } catch (error) {
      console.error("Error fetching CBT scores:", error);
      alert("An error occurred while fetching the scores.");
    } finally {
      setLoading(false);
    }
  };

  const canSelectClass = userData && ['admin', 'principal'].includes(userData.role?.toLowerCase());

  return (
    <MainLayout>
      <div className="view-cbt-scores-container">
        <h4>View CBT Test Scores</h4>
        
        <div className="filters-container row mb-3">
            <div className="col-md-3">
                <label>Session</label>
                <select className="form-control" value={selectedSession} onChange={e => setSelectedSession(e.target.value)}>
                    {sessions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <div className="col-md-3">
                <label>Term</label>
                <select className="form-control" value={selectedTerm} onChange={e => setSelectedTerm(e.target.value)}>
                    {terms.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>
            <div className="col-md-3">
                <label>Class</label>
                <select className="form-control" value={selectedClass} onChange={e => setSelectedClass(e.target.value)} disabled={!canSelectClass}>
                {canSelectClass ? (
                    <>
                    <option value="">-- Select Class --</option>
                    {allClasses.map(c => <option key={c} value={c}>{c}</option>)}
                    </>
                ) : (
                    <option value={selectedClass}>{selectedClass}</option>
                )}
                </select>
            </div>
            <div className="col-md-3">
                <label>Subject</label>
                <select className="form-control" value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
                <option value="">-- Select Subject --</option>
                {Array.isArray(assignedSubjects) && assignedSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
        </div>

        <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
          {loading ? 'Searching...' : 'Search'}
        </button>

        {cbtScores.length > 0 && (
            <div className="mt-4">
                <h5>CBT Test Scores for {selectedSubject} in {selectedClass}</h5>
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Student Name</th>
                            <th>Reg No</th>
                            <th>Score</th>
                            <th>Out of</th>
                            <th>Date Taken</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cbtScores.map((item, index) => (
                            <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td>{item.name}</td>
                                <td>{item.regNo}</td>
                                <td>{item.score}</td>
                                <td>{item.totalQuestions}</td>
                                <td>{item.timestamp}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )}

      </div>
    </MainLayout>
  );
};

export default ViewCBTTestScores;
